import { WorkflowResult } from '../api/client';

interface SellerDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: 'qualified' | 'unqualified' | 'noResults' | 'errors';
  sellers: WorkflowResult['results'];
}

export default function SellerDetailsModal({ isOpen, onClose, category, sellers }: SellerDetailsModalProps) {
  if (!isOpen) return null;

  const getCategoryTitle = () => {
    switch (category) {
      case 'qualified':
        return 'Qualified Sellers';
      case 'unqualified':
        return 'Unqualified Sellers';
      case 'noResults':
        return 'No Results';
      case 'errors':
        return 'Errors';
    }
  };

  const getCategoryDescription = () => {
    switch (category) {
      case 'qualified':
        return 'These sellers meet the qualification thresholds and are ready for outreach.';
      case 'unqualified':
        return 'These sellers do not meet the minimum qualification thresholds.';
      case 'noResults':
        return 'No eBay listings were found for these UPCs.';
      case 'errors':
        return 'These UPCs encountered errors during processing.';
    }
  };

  const getStatusIcon = (status: boolean) => {
    return status ? '✅' : '❌';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getCategoryTitle()}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <p className="modal-description">{getCategoryDescription()}</p>

        <div className="modal-body">
          {sellers.length === 0 ? (
            <p className="empty-message">No sellers in this category.</p>
          ) : (
            <div className="seller-details-list">
              {sellers.map((seller, index) => (
                <div key={index} className="seller-detail-card">
                  <div className="seller-header">
                    <div>
                      <h3>{seller.sellerUsername}</h3>
                      <p className="product-title">{seller.productTitle}</p>
                      <p className="upc-code">UPC: {seller.upc}</p>
                      {seller.itemUrl && (
                        <a href={seller.itemUrl} target="_blank" rel="noopener noreferrer" className="view-listing">
                          View Listing →
                        </a>
                      )}
                    </div>
                    <span className={`status-badge status-${category}`}>
                      {seller.qualificationStatus}
                    </span>
                  </div>

                  {seller.sellerDetails && (
                    <div className="seller-metrics">
                      <div className="metrics-grid">
                        <div className="metric">
                          <label>Feedback Score</label>
                          <strong>{seller.sellerDetails.feedbackScore.toLocaleString()}</strong>
                        </div>
                        <div className="metric">
                          <label>Positive Feedback</label>
                          <strong>{seller.sellerDetails.positiveFeedbackPercent.toFixed(1)}%</strong>
                        </div>
                        <div className="metric">
                          <label>Account Age</label>
                          <strong>{seller.sellerDetails.accountAgeYears.toFixed(1)} years</strong>
                        </div>
                        <div className="metric">
                          <label>Est. Items Sold</label>
                          <strong>{seller.sellerDetails.estimatedItemsSold.toLocaleString()}</strong>
                        </div>
                      </div>

                      <div className="thresholds">
                        <h4>Qualification Thresholds</h4>
                        <div className="threshold-list">
                          <div className="threshold-item">
                            <span className="threshold-icon">
                              {getStatusIcon(seller.sellerDetails.meetsThresholds.feedback)}
                            </span>
                            <span>Positive Feedback ≥90%</span>
                            <span className="threshold-value">
                              ({seller.sellerDetails.positiveFeedbackPercent.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="threshold-item">
                            <span className="threshold-icon">
                              {getStatusIcon(seller.sellerDetails.meetsThresholds.itemsSold)}
                            </span>
                            <span>Estimated Items Sold ≥500</span>
                            <span className="threshold-value">
                              (~{seller.sellerDetails.estimatedItemsSold.toLocaleString()})
                            </span>
                          </div>
                          <div className="threshold-item">
                            <span className="threshold-icon">
                              {getStatusIcon(seller.sellerDetails.meetsThresholds.accountAge)}
                            </span>
                            <span>Account Age ≥2 years</span>
                            <span className="threshold-value">
                              ({seller.sellerDetails.accountAgeYears.toFixed(1)} years)
                            </span>
                          </div>
                        </div>
                      </div>

                      {seller.sellerDetails.qualificationReason && (
                        <div className="qualification-reason">
                          <h4>Analysis</h4>
                          <p>{seller.sellerDetails.qualificationReason}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {seller.errorDetails && (
                    <div className="error-details">
                      <h4>Error Details</h4>
                      <p>{seller.errorDetails}</p>
                    </div>
                  )}

                  {seller.qualificationStatus === 'Qualified' && seller.outreachMessage && (
                    <div className="outreach-message">
                      <h4>Outreach Message</h4>
                      <p className="message-preview">{seller.outreachMessage}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

